import Vue from 'vue';

/* Global store */
const store = Vue.observable({
	/* Current user selection for quote */
	selection: {
		compare: {
			/* Selected tier for Compare & Deploy */
			/* [ starter, teams, enterprise ] or blank for none */
			tier: '',

			/* Number of Gearset users */
			users: 0,
		},

		automation: {
			/* Selected tier for Automation */
			/* [ starter, teams, enterprise ] or blank for none */
			tier: '',
		},

		/* Selected addons */
		/* Keys for addon names, value boolean */
		addons: {},

		backup: {
			/* Selected tier for Data Backup */
			/* [ starter, teams, enterprise ] or blank for none */
			tier: '',

			/* Number of Salesforce users for Backup */
			users: 0,
		},

		flowErrorMonitoring: {
			/* Selected tier for Flow Error Monitoring */
			/* 'monitoring' or blank for none */
			tier: '',

			/* Number of Salesforce users for Flow Error Monitoring */
			users: 0,
		},

		codeReviews: {
			/* Selected tier for Code reviews */
			/* 'monitoring' or blank for none */
			tier: '',

			/* Number of individual contributors for Code reviews */
			users: 0,
		},

		/* Total monthly cost in cents */
		total: 0,
	},

	/* Product information */
	data: {
		/* Compare & deploy */
		compare: {
			name: 'Compare and Deploy',
			label: 'Compare and deploy metadata',

			per: 'per Gearset user, per month',

			userType: 'Gearset user',

			features: {
				'compare-deploy': {
					label: 'Compare and deploy metadata',
				},
				'source-control': {
					label: 'Source control for all Git providers',
				},
				'deployment-history': {
					label: 'Full deployment history',
				},
				'clone-packages': {
					label: 'Clone packages',
				},
				'deployment-reports': {
					label: 'Deployment reports',
				},
				'unlimited-orgs': {
					label: 'Unlimited Production orgs',
				},
				'live-chat-support': {
					label: 'Live chat support',
				},
				'scheduled-deployments': {
					label: 'Scheduled deployments',
				},
				'deployment-rollback': {
					label: 'Deployment roll-back',
				},
				'combine-redeploy': {
					label: 'Combine and redeploy packages',
				},
				'role-based-access-control': {
					label: 'Role-based access control',
				},
				'issue-tracking': {
					label: 'Issue tracking integration',
				},
				'team-connections': {
					label: 'Team-shared connections',
				},
				saml: {
					label: 'SAML',
				},
			},

			products: {
				starter: {
					name: 'Starter',
					description: 'Deployment features for less complex Salesforce environments with simpler requirements',
					productCode: 'GEARSETDEPLOYMENTSOLUTIONSTARTER',
					price: 20000,
					features: {
						'compare-deploy': true,
						'source-control': true,
						'deployment-history': true,
						'clone-packages': true,
						'deployment-reports': true,
						'unlimited-orgs': true,
						'live-chat-support': true,
					},
				},
				teams: {
					name: 'Teams',
					description: 'Advanced deployment features for more complex Salesforce release management',
					productCode: 'GEARSETDEPLOYMENTSOLUTIONTEAMS',
					price: 30000,
					features: {
						'compare-deploy': true,
						'source-control': true,
						'deployment-history': true,
						'clone-packages': true,
						'deployment-reports': true,
						'unlimited-orgs': true,
						'live-chat-support': true,
						'scheduled-deployments': true,
						'deployment-rollback': true,
						'combine-redeploy': true,
						'role-based-access-control': true,
						'issue-tracking': true,
						'team-connections': true,
					},
				},
				enterprise: {
					name: 'Enterprise',
					productCode: 'GEARSETDEPLOYMENTSOLUTIONENTERPRISE',
					requiresCustomQuote: true,
					features: {
						'compare-deploy': true,
						'source-control': true,
						'deployment-history': true,
						'clone-packages': true,
						'deployment-reports': true,
						'unlimited-orgs': true,
						'live-chat-support': true,
						'scheduled-deployments': true,
						'deployment-rollback': true,
						'combine-redeploy': true,
						'role-based-access-control': true,
						'issue-tracking': true,
						'team-connections': true,
						saml: true,
					},
				},
			},
		},

		/* Automation */
		automation: {
			name: 'Automation',
			label: 'Choose the amount of automation for your needs',

			per: 'per team, per month',

			features: {
				'monitoring-jobs': {
					label: 'Change monitoring jobs',
					type: 'numerical',
					unlimitedLabel: 'Unlimited',
				},
				'monitoring-frequency': {
					label: 'Change monitoring frequency',
					type: 'boolean',
					value: 'Daily',
				},
				'production-orgs': {
					label: 'Production orgs',
					type: 'numerical',
					unlimitedLabel: 'Unlimited',
				},
				'cicd-jobs': {
					label: 'CI/CD',
					type: 'numerical',
					unit: 'orgs',
					unlimitedLabel: 'Unlimited',
				},
				'audit-api': {
					label: 'API calls (Audit & reporting)',
					type: 'boolean',
					value: 'Included',
				},
				'unit-testing': {
					label: 'Unit testing',
					type: 'numerical',
					unlimitedLabel: 'Unlimited',
				},
				'automation-api': {
					label: 'API calls (Automation)',
					type: 'boolean',
					value: 'Included',
				},
				'gearset-pipelines': {
					label: 'Gearset Pipelines',
					type: 'boolean',
					value: 'Included',
				},
				'sandbox-syncing': {
					label: 'Sandbox syncing',
					type: 'boolean',
					value: 'Included',
				},
				'ui-testing-integrations': {
					label: 'UI testing integrations',
					type: 'boolean',
					value: 'Included',
				},
				'long-term-project-workflows': {
					label: 'Long term project workflows',
					type: 'boolean',
					value: 'Included',
				},
			},

			products: {
				starter: {
					name: 'Starter',
					productCode: 'AUTOMATIONPLATFORMSTARTER',
					price: 50000,
					features: {
						'monitoring-jobs': 5,
						'monitoring-frequency': true,
						'production-orgs': 1,
						'cicd-jobs': 5,
						'unit-testing': -1,
						'automation-api': true,
						'gearset-pipelines': true,
						'sandbox-syncing': true,
					},
				},
				teams: {
					name: 'Teams',
					productCode: 'AUTOMATIONPLATFORMTEAMS',
					price: 100000,
					features: {
						'monitoring-jobs': 15,
						'monitoring-frequency': true,
						'production-orgs': 1,
						'cicd-jobs': 15,
						'audit-api': true,
						'unit-testing': -1,
						'automation-api': true,
						'gearset-pipelines': true,
						'sandbox-syncing': true,
						'ui-testing-integrations': true,
					},
				},
				enterprise: {
					name: 'Enterprise',
					productCode: 'AUTOMATIONPLATFORMENTERPRISE',
					requiresCustomQuote: true,
					features: {
						'monitoring-jobs': -1,
						'monitoring-frequency': true,
						'production-orgs': -1,
						'cicd-jobs': -1,
						'audit-api': true,
						'unit-testing': -1,
						'automation-api': true,
						'gearset-pipelines': true,
						'sandbox-syncing': true,
						'ui-testing-integrations': true,
						'long-term-project-workflows': true,
					},
				},
			},
		},

		/* Add-ons */
		addons: {
			name: 'Addons',
			label: 'Add specialised functionality',

			per: 'net spend',

			products: {
				cpq: {
					name: 'CPQ+ and Billing',
					label: 'Include Revenue cloud',
					productCode: 'CPQ',
					priceMultiplier: 0.4,
					priceMinimum: 50000,
					features: [
						'Compare and deploy CPQ configuration',
						'Deploy Advanced Approvals',
						'Deploy Billing',
						'Deploy alongside your metadata',
					],
				},
				vlocity: {
					name: 'Salesforce Industries',
					label: 'Include SF Industries',
					productCode: 'SALESFORCEINDUSTRIES',
					priceMultiplier: 0.4,
					priceMinimum: 50000,
					features: [
						'Deploy Vlocity',
						'Treat config data as metadata and deploy faster',
						'Compare and deploy from source control',
						'Roll back data configuration changes',
					],
				},
				sandbox: {
					name: 'Sandbox seeding and compliance',
					label: 'Add Sandbox seeding',
					productCode: 'SANDBOXSEEDING',
					priceMultiplier: 0.4,
					features: [
						'Configure from/to any org',
						'Automatic mapping of relationships',
						'Data templates for faster deployments',
						'Disable triggers automatically',
						'Data masking',
					],
				},
				archiving: {
					name: 'Archiving',
					label: 'Add Archiving',
					productCode: 'ARCHIVING',
					requiresCustomQuote: true,
					features: [
						'Customizable archiving policies',
						'Quick restore',
						'Automated archiving',
						'GDPR and CCPA/CPRA compliant',
						'Data securely stored in Gearset',
					],
				},
			},
		},

		/* Backup */
		backup: {
			name: 'Data Backup',
			label: 'Include Data Backup',

			per: 'per Salesforce user, per month',

			userType: 'Salesforce user',
			usersMinimum: 100,

			features: {
				'standard-jobs': {
					label: 'Standard',
					type: 'numerical',
					unit: 'jobs',
					unlimitedLabel: 'Custom',
				},
				'high-frequency-jobs': {
					label: 'High frequency',
					type: 'numerical',
					unit: 'jobs',
					unlimitedLabel: 'Custom',
				},
				'automated-backup': {
					label: 'Automated daily backup',
				},
				'restore-tools': {
					label: 'Data restore tools',
				},
				'metadata-restore': {
					label: 'Metadata restore',
				},
				'auditing-tools': {
					label: 'Auditing',
				},
				'team-permissions': {
					label: 'Team member permissions',
				},
				'key-management': {
					label: 'Self-service encryption key management',
				},
				'advanced-deploys': {
					label: 'Advanced data and metadata deploys',
				},
				'change-monitoring': {
					label: 'Metadata change monitoring',
				},
				'high-frequency-backup': {
					label: 'High frequency backup',
				},
				'team-connections': {
					label: 'Team-shared connections',
				},
				'search-backups': {
					label: 'Search backups',
				},
				saml: {
					label: 'SAML',
				},
				byok: {
					label: 'Bring your own key (BYOK)',
				},
				'hipaa-baa': {
					label: 'HIPAA BAA',
				},
			},

			products: {
				starter: {
					name: 'Starter',
					productCode: 'DATABACKUPSTARTER',
					price: 250,
					priceMinimum: 25000,
					features: {
						'standard-jobs': 2,
						'high-frequency-jobs': 0,
						'automated-backup': true,
						'restore-tools': true,
						'metadata-restore': true,
						'auditing-tools': true,
						'team-permissions': true,
						'key-management': true,
					},
				},
				teams: {
					name: 'Teams',
					productCode: 'DATABACKUPTEAMS',
					price: 325,
					priceMinimum: 32500,
					features: {
						'standard-jobs': 2,
						'high-frequency-jobs': 2,
						'automated-backup': true,
						'restore-tools': true,
						'metadata-restore': true,
						'auditing-tools': true,
						'team-permissions': true,
						'key-management': true,
						'advanced-deploys': true,
						'change-monitoring': true,
						'high-frequency-backup': true,
						'team-connections': true,
						'search-backups': true,
					},
				},
				enterprise: {
					name: 'Enterprise',
					productCode: 'DATABACKUPENTERPRISE',
					requiresCustomQuote: true,
					features: {
						'standard-jobs': -1,
						'high-frequency-jobs': -1,
						'automated-backup': true,
						'restore-tools': true,
						'metadata-restore': true,
						'auditing-tools': true,
						'team-permissions': true,
						'key-management': true,
						'advanced-deploys': true,
						'change-monitoring': true,
						'high-frequency-backup': true,
						'team-connections': true,
						'search-backups': true,
						saml: true,
						byok: true,
						'hipaa-baa': true,
					},
				},
			},
		},

		/* Flow error monitoring */
		flowErrorMonitoring: {
			name: 'Flow error monitoring',
			label: 'Include Flow error monitoring',
			tiersLabel: false,

			per: 'per Salesforce user, per month',

			userType: 'Salesforce user',
			usersMinimum: 100,

			features: {
				'flow-error-monitoring': {
					label: '24/7 Flow error monitoring',
				},
				'flow-error-analysis': {
					label: 'Flow error analysis and visualization',
				},
				'alert-notification': {
					label: 'Custom alert notification rules',
				},
				'slack-teams-notification': {
					label: 'Slack and Teams notification support',
				},
				'search-errors': {
					label: 'Search errors by Flow name, element and error type',
				},
			},

			products: {
				monitoring: {
					name: 'Include',
					productCode: 'FLOWERRORMONITORING',
					requiresCustomQuote: true,
					features: {
						'flow-error-monitoring': true,
						'flow-error-analysis': true,
						'alert-notification': true,
						'slack-teams-notification': true,
						'search-errors': true,
					},
				},
			},
		},

		/* Clayton */
		codeReviews: {
			name: 'Code reviews',
			label: 'Include Code reviews',
			tiersLabel: false,

			per: 'per individual contributor, per month',

			userType: 'individual contributor',

			features: {
				'comprehensive-scanning': {
					label: 'Comprehensive scanning library, including Apex, Flows, LWCs & more',
				},
				'git-integration': {
					label: 'Git integration for enforced quality gates',
				},
				'automated-fixes': {
					label: 'Automated fixes',
				},
				'customizable-rules': {
					label: 'Customizable rules and policies',
				},
				reporting: {
					label: 'Reporting on org & repo health',
				},
				'team-performance': {
					label: 'Team performance insights',
				},
			},

			products: {
				monitoring: {
					name: 'Include',
					productCode: 'CLAYTON',
					price: 15000,
					features: {
						'comprehensive-scanning': true,
						'git-integration': true,
						'automated-fixes': true,
						'customizable-rules': true,
						reporting: true,
						'team-performance': true,
					},
				},
			},
		},
	},
});

export default {
	store,
	install(Vue) {
		Vue.prototype.$store = store;
	},
};
